/**
 * components/PageHeader
 * The h1 block at the top of every screen: 20px/600 title with a 2px gap to a
 * 12.5px grey subtitle, and 18px of space beneath the block.
 *
 * `right` holds the screen's own controls — the register's search and scope
 * chips, the custodian's assignment switcher, the approver's "Approve all".
 * They wrap below the title on a narrow viewport rather than squeezing it.
 */
import type { ReactNode } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { type as textRole } from '../../theme/typography';
import { useResponsive } from '../../lib/responsive';

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  right?: ReactNode;
}

export function PageHeader({ title, subtitle, right }: PageHeaderProps) {
  const { isMobile } = useResponsive();

  return (
    <View style={[styles.header, isMobile && styles.stacked]}>
      <View style={styles.titles}>
        <Text style={textRole.pageTitle}>{title}</Text>
        {!!subtitle && <Text style={[textRole.pageSubtitle, styles.subtitle]}>{subtitle}</Text>}
      </View>
      {!!right && <View style={[styles.right, isMobile && styles.rightStacked]}>{right}</View>}
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 14,
    marginBottom: 18,
  },
  stacked: { flexDirection: 'column', alignItems: 'stretch' },
  titles: { flex: 1, minWidth: 0 },
  subtitle: { marginTop: 2 },
  right: { flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap' },
  rightStacked: { flexShrink: 1 },
});

export default PageHeader;
